import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SeatList, SeatMapView, shapesOf, shapesToGeometryJson, useSeatSelection } from '@bre-ticketing/seatmap';
import type { ManifestGeometry, Point, PolygonTool, SeatGeometry } from '@bre-ticketing/seatmap';
import { ApiError } from '../../api';
import type { Canvas, ManifestLayout, SeatUpdate } from '../../api';
import { endpoints } from '../../api';
import { Button, Panel } from '../../ui';
import { INPUT } from './forms';

/**
 * The map itself, with the list view beside it and — for anyone allowed to
 * edit — the selection inspector: mark seats accessible or restricted, link
 * companions, nudge or drag positions, add a seat, remove seats. Rows can be
 * dragged by their letter and extended from their end handles. Section and GA
 * boundaries are drawn here too. Structure changes that need a form (new
 * rows, tables, sections) come from the page; this panel is about what is
 * already on the map.
 */

type Flag = 'accessible' | 'companion' | 'restricted_view' | 'obstructed_view' | 'production_kill_capable' | 'aisle';

interface BoundaryTarget {
  kind: 'section' | 'ga';
  id: string;
  name: string;
}



/**
 * Which part of the drawing to show (D-267).
 *
 * A canvas used to be a size with an implied origin of 0,0, so the only way to
 * trim one was to make it smaller -- which crops the bottom. A room imported
 * from an artboard that is mostly empty *above* the seats could not be framed
 * at all: shrinking it cut off the stage rather than the emptiness.
 *
 * **Fit to content is the button people will actually use.** Typing four
 * numbers to frame a room is a guessing game, so the server works the box out
 * from what is drawn and this fills the fields with it -- unsaved, so it can be
 * looked at and undone before it is committed.
 */
function CanvasPanel({ manifestId, layout, onChanged, onError, onPreview }: { manifestId: string; layout: ManifestGeometry; onChanged: () => void; onError: (error: ApiError) => void; onPreview: (canvas: Canvas | null) => void }) {
  const [draft, setDraft] = useState({
    canvas_x: layout.manifest.canvas_x ?? 0,
    canvas_y: layout.manifest.canvas_y ?? 0,
    canvas_width: layout.manifest.canvas_width,
    canvas_height: layout.manifest.canvas_height,
  });
  const [dirty, setDirty] = useState(false);

  const fit = useMutation({
    mutationFn: () => endpoints.manifestCanvasFit(manifestId),
    onSuccess: (result) => {
      setDraft(result.fit);
      setDirty(true);
      onPreview(result.fit);
    },
    onError: (cause: ApiError) => onError(cause),
  });
  const save = useMutation({
    mutationFn: () => endpoints.setManifestCanvas(manifestId, draft),
    onSuccess: () => {
      setDirty(false);
      // The saved layout is about to be refetched; drop the preview so the map
      // shows what is actually stored rather than a copy of it.
      onPreview(null);
      onChanged();
    },
    onError: (cause: ApiError) => onError(cause),
  });

  const field = (key: keyof typeof draft, label: string) => (
    <label className="block">
      <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</span>
      <input
        type="number"
        value={draft[key]}
        onChange={(e) => {
          const next = { ...draft, [key]: Number(e.target.value) || 0 };
          setDraft(next);
          setDirty(true);
          onPreview(next);
        }}
        className={`${INPUT} mt-1 w-full`}
      />
    </label>
  );

  return (
    <Panel title="Canvas" description="Which part of the drawing the map shows. Nothing here moves a seat — it is the window, not the room — so it can be changed on a published manifest and set back again.">
      <div className="grid gap-3 px-5 py-4 sm:grid-cols-5">
        {field('canvas_x', 'Left')}
        {field('canvas_y', 'Top')}
        {field('canvas_width', 'Width')}
        {field('canvas_height', 'Height')}
        <div className="flex items-end gap-2">
          <Button onClick={() => fit.mutate()} disabled={fit.isPending}>
            Fit to content
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-3 border-t border-line px-5 py-3">
        <Button variant="primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          Save canvas
        </Button>
        {dirty ? <span className="text-sm text-ink-600">Not saved yet — the map above already shows it.</span> : null}
      </div>
    </Panel>
  );
}

export function SeatMapPanel({
  manifestId,
  layout,
  canEditStructure,
  canEditAttributes,
  placing,
  onPlace,
  onChanged,
  onError,
}: {
  manifestId: string;
  layout: ManifestLayout;
  canEditStructure: boolean;
  canEditAttributes: boolean;
  /** A form on the page is waiting for a click on the map. */
  placing: boolean;
  onPlace: (point: Point) => void;
  onChanged: () => void;
  onError: (error: ApiError) => void;
}) {
  const [view, setView] = useState<'map' | 'list'>('map');
  const [note, setNote] = useState('');
  const [step, setStep] = useState('5');
  const [rotation, setRotation] = useState('0');
  const [linking, setLinking] = useState<string | null>(null);
  const [addingSeatTo, setAddingSeatTo] = useState<{ row_id?: string; table_id?: string; label: string } | null>(null);
  /** `at` says which shape of the pool the tool is working on (D-184). */
  const [boundary, setBoundary] = useState<{ target: BoundaryTarget; tool: PolygonTool | null; at: number | 'append' } | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const selection = useSeatSelection();

  /*
   * The map draws the *unsaved* canvas while somebody is adjusting it (D-267).
   * Framing a room by typing four numbers and pressing save to find out is not
   * a thing anyone should have to do twice.
   */
  /*
   * Undo (D-271). One step, server side, because the step that most needs
   * putting back is a *delete* -- and a browser could only re-create the seat
   * with a new id, orphaning the subscriptions keyed on the old one.
   *
   * Only on a DRAFT room: a published manifest refuses every structural change
   * by trigger, so there is nothing here to put back.
   */
  const undo = useMutation({
    mutationFn: () => endpoints.undoManifestEdit(manifestId),
    onSuccess: () => onChanged(),
    onError: (cause: ApiError) => onError(cause),
  });
  const edits = useQuery({ queryKey: ['manifest-edits', manifestId], queryFn: () => endpoints.manifestEdits(manifestId) });
  /*
   * What is wrong with this room (D-274). Read-only, so it runs on a published
   * manifest too: knowing a published room has two seats in the same place is
   * more useful than being told the room cannot be changed.
   */
  const lint = useQuery({ queryKey: ['manifest-lint', manifestId], queryFn: () => endpoints.lintManifest(manifestId) });
  const problems = lint.data?.problems ?? [];
  const nextUndo = (edits.data?.edits ?? []).find((e) => !e.undone_at) ?? null;

  const [canvasPreview, setCanvasPreview] = useState<Canvas | null>(null);
  const geometry = useMemo(() => {
    const base = layout as unknown as ManifestGeometry;
    return canvasPreview ? { ...base, manifest: { ...base.manifest, ...canvasPreview } } : base;
  }, [layout, canvasPreview]);
  const seatsById = useMemo(() => new Map(layout.seats.map((seat) => [seat.id, seat])), [layout.seats]);
  const selectedSeats = useMemo(
    () => Array.from(selection.selected, (id) => seatsById.get(id)).filter((seat): seat is NonNullable<typeof seat> => Boolean(seat)),
    [selection.selected, seatsById],
  );
  const editing = canEditAttributes && selectedSeats.length > 0 && !boundary?.tool;

  const fail = (error: unknown) => onError(error instanceof ApiError ? error : new ApiError(0, 'UNKNOWN', String(error)));

  const update = useMutation({
    mutationFn: ({ ids, set }: { ids: string[]; set: SeatUpdate }) => endpoints.updateSeats(manifestId, ids, set),
    onSuccess: onChanged,
    onError: fail,
  });
  const updateSelection = (set: SeatUpdate) => update.mutate({ ids: Array.from(selection.selected), set });

  /*
   * The bulk operations (D-273). Safe to offer only because undo exists: each
   * of these reshapes the whole selection from one click, and the arithmetic
   * lives in `@bre-ticketing/seatmap` so the button here is a call and not a
   * second implementation.
   */
  const arrange = useMutation({
    mutationFn: (input: Parameters<typeof endpoints.arrangeSeats>[1]) => endpoints.arrangeSeats(manifestId, input),
    onSuccess: () => onChanged(),
    onError: (cause: ApiError) => onError(cause),
  });
  const renumber = useMutation({
    mutationFn: (input: Parameters<typeof endpoints.renumberSeats>[1]) => endpoints.renumberSeats(manifestId, input),
    onSuccess: () => onChanged(),
    onError: (cause: ApiError) => onError(cause),
  });
  const ids = () => Array.from(selection.selected);
  const askRenumber = () => {
    const first = window.prompt('Start numbering at', '1');
    if (first === null) return;
    const numbering = window.confirm('Odd numbers only? (Cancel for every number)') ? 'ODD' : 'SEQUENTIAL';
    const reverse = window.confirm('Number right to left? (Cancel for left to right)');
    // 13 is the one every venue asks about, so it is offered rather than typed.
    const skip = window.confirm('Skip 13?') ? [13] : undefined;
    /*
     * Which order to read the seats in (D-287). An imported room has no order
     * of its own -- an SVG gave every seat `sort_order = 1` -- so numbering by
     * it numbers them at random. Reading off the map also repairs that column,
     * which is what best available walks.
     */
    const order = window.confirm(`Read the seats off the map, left to right?

OK — use where the seats are (fixes an imported room)
Cancel — use the room’s own seat order`)
      ? 'POSITION'
      : 'SORT_ORDER';
    renumber.mutate({ seat_ids: ids(), first: Number(first) || 1, numbering: numbering as 'ODD' | 'SEQUENTIAL', reverse, order, ...(skip ? { skip } : {}) });
  };

  /**
   * Rename the row the selection is in (D-290).
   *
   * Offered when every selected seat shares a row, which is how somebody
   * fixing an import is already working -- they have just renumbered it. An
   * SVG import names rows after the elements it found them in, and `_102-A` is
   * what a customer reads on a ticket.
   */
  const renameRow = useMutation({
    mutationFn: ({ rowId, label }: { rowId: string; label: string }) => endpoints.updateRow(manifestId, rowId, { label }),
    onSuccess: () => onChanged(),
    onError: (cause: ApiError) => onError(cause),
  });
  const selectedRowId = (() => {
    const rows = new Set(selectedSeats.map((seat) => seat.row_id).filter(Boolean));
    return rows.size === 1 ? (selectedSeats[0]?.row_id ?? null) : null;
  })();
  const askRenameRow = (rowId: string | null) => {
    if (!rowId) return;
    const current = layout.rows?.find((r) => r.id === rowId)?.label ?? '';
    const next = window.prompt('Row name', String(current));
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === String(current)) return;
    renameRow.mutate({ rowId, label: trimmed });
  };

  /** Rename one seat (D-287): the touch-up after an import, or a seat called 12A. */
  const askRename = () => {
    const seat = selectedSeats[0];
    if (!seat) return;
    const next = window.prompt('Seat number', String(seat.seat_number ?? ''));
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === String(seat.seat_number ?? '')) return;
    update.mutate({ ids: [seat.id], set: { seat_number: trimmed } });
  };

  // A table moves as one piece: the shape and every seat around it (D-72).
  const moveTable = useMutation({
    mutationFn: ({ tableId, set }: { tableId: string; set: { dx?: number; dy?: number; rotation?: number; label?: string } }) => endpoints.updateTable(manifestId, tableId, set),
    onSuccess: onChanged,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => endpoints.deleteSeats(manifestId, Array.from(selection.selected)),
    onSuccess: () => {
      selection.clear();
      onChanged();
    },
    onError: fail,
  });

  const link = useMutation({
    mutationFn: ({ companionId, targetId }: { companionId: string; targetId: string }) =>
      endpoints.updateSeats(manifestId, [companionId], { companion_for_seat_id: targetId }),
    onSuccess: () => {
      setLinking(null);
      onChanged();
    },
    onError: (error: ApiError) => {
      setLinking(null);
      onError(error);
    },
  });

  /** Extending asks for numbers only when the row's own scheme cannot say. */
  const extend = useMutation({
    mutationFn: async ({ rowId, count, end }: { rowId: string; count: number; end: 'END' | 'START' }) => {
      try {
        return await endpoints.extendRow(manifestId, rowId, { count, end });
      } catch (error) {
        if (!(error instanceof ApiError) || error.code !== 'NUMBERING_AMBIGUOUS') throw error;
        const answer = window.prompt(
          `This row's numbering cannot be continued automatically (it has ${(error.details?.existing as string[] | undefined)?.join(', ') ?? 'seats'}). Enter ${count} seat number(s), comma separated, from the existing ${end === 'END' ? 'end' : 'start'} outward:`,
        );
        if (!answer) throw new ApiError(0, 'CANCELLED', 'No seats were added');
        const seat_numbers = answer.split(',').map((s) => s.trim()).filter(Boolean);
        return await endpoints.extendRow(manifestId, rowId, { count: seat_numbers.length, end, seat_numbers });
      }
    },
    onSuccess: onChanged,
    onError: fail,
  });

  const addSeat = useMutation({
    mutationFn: async (point: Point) => {
      if (!addingSeatTo) return;
      const target = addingSeatTo.row_id ? { row_id: addingSeatTo.row_id } : { table_id: addingSeatTo.table_id };
      try {
        return await endpoints.addSeat(manifestId, { ...target, x: Math.round(point[0]), y: Math.round(point[1]) });
      } catch (error) {
        if (!(error instanceof ApiError) || error.code !== 'SEAT_NUMBER_REQUIRED') throw error;
        const between = (error.details?.between as [string | null, string | null] | undefined) ?? [null, null];
        const answer = window.prompt(`What number is this seat? It lands between ${between[0] ?? 'the start'} and ${between[1] ?? 'the end'}.`);
        if (!answer?.trim()) throw new ApiError(0, 'CANCELLED', 'No seat was added');
        return await endpoints.addSeat(manifestId, { ...target, x: Math.round(point[0]), y: Math.round(point[1]), seat_number: answer.trim() });
      }
    },
    onSuccess: () => {
      setAddingSeatTo(null);
      onChanged();
    },
    onError: fail,
  });

  /**
   * Save one shape of an outline (D-184).
   *
   * An outline may hold several shapes that are one pool of capacity — a lawn
   * split by a walkway, standing room down both sides — so saving means
   * replacing one of them, adding one, or taking one away, not overwriting the
   * lot. One shape is still stored as a plain polygon, so nothing already drawn
   * is rewritten just because the editor learned to hold more.
   */
  const saveBoundary = useMutation({
    mutationFn: ({ target, points, at }: { target: BoundaryTarget; points: Point[] | null; at: number | 'append' | 'all' }): Promise<unknown> => {
      const existing = shapesFor(target);
      const next =
        points === null
          ? at === 'all'
            ? []
            : existing.filter((_, index) => index !== at)
          : at === 'append'
            ? [...existing, points]
            : existing.map((shape, index) => (index === at ? points : shape));
      const geometry_json = shapesToGeometryJson(next);
      return target.kind === 'section'
        ? endpoints.updateSection(manifestId, target.id, { geometry_json })
        : endpoints.updateGaArea(manifestId, target.id, { geometry_json });
    },
    onSuccess: () => {
      setBoundary(null);
      onChanged();
    },
    onError: fail,
  });

  const all = (key: Flag) => selectedSeats.length > 0 && selectedSeats.every((seat) => seat[key] === 1);
  const toggle = (key: Flag) => updateSelection({ [key]: !all(key) } as SeatUpdate);

  const onSeatClick = (seat: SeatGeometry, modifiers: { shiftKey: boolean; metaKey: boolean }) => {
    if (linking) {
      link.mutate({ companionId: linking, targetId: seat.id });
      return;
    }
    selection.clickSeat(seat, modifiers);
  };

  const onCanvasClick = (point: Point) => {
    if (addingSeatTo) addSeat.mutate(point);
    else if (placing) onPlace(point);
  };
  const canvasClickActive = Boolean(addingSeatTo) || placing;

  // --- Boundary state -------------------------------------------------------

  const boundaryOptions: BoundaryTarget[] = [
    ...layout.sections.map((s) => ({ kind: 'section' as const, id: s.id, name: `Section ${s.name}` })),
    ...layout.ga_areas.map((a) => ({ kind: 'ga' as const, id: a.id, name: `GA ${a.name}` })),
  ];
  const geometryOf = (target: BoundaryTarget): string | null =>
    (target.kind === 'section' ? layout.sections.find((s) => s.id === target.id)?.geometry_json : layout.ga_areas.find((a) => a.id === target.id)?.geometry_json) ?? null;
  const shapesFor = (target: BoundaryTarget): Point[][] => shapesOf(geometryOf(target));
  const derived = (target: BoundaryTarget): boolean => {
    try {
      return (JSON.parse(geometryOf(target) ?? 'null') as { source?: string } | null)?.source === 'seats';
    } catch {
      return false;
    }
  };
  const boundaryStatus = (target: BoundaryTarget): string => {
    const shapes = shapesFor(target);
    if (shapes.length === 0) return target.kind === 'section' ? 'no outline yet (needs three seats, or draw one)' : 'no outline yet';
    if (derived(target)) return 'follows the seats';
    return shapes.length === 1 ? 'drawn by hand' : `${shapes.length} shapes, one pool`;
  };

  const Flag = ({ label, flag }: { label: string; flag: Flag }) => (
    <label className="flex items-center gap-1.5 text-sm text-ink-900">
      <input type="checkbox" checked={all(flag)} onChange={() => toggle(flag)} disabled={update.isPending} />
      {label}
    </label>
  );

  const single = selectedSeats.length === 1 ? selectedSeats[0]! : null;
  const singleRow = single?.row_id ? layout.rows.find((row) => row.id === single.row_id) : null;
  const singleTable = single?.table_id ? layout.tables.find((table) => table.id === single.table_id) : null;

  const description =
    layout.seats.length === 0 && layout.ga_areas.length === 0
      ? 'Nothing drawn yet. Import a drawing, or add a section and generate rows.'
      : canEditAttributes
        ? 'Click a seat, shift-click to add, shift-drag to select many. Drag seats or a row letter to move them. Click a section outline to reshape it. Attributes can be corrected even after publishing; structure cannot.'
        : 'What a buyer will see, without availability.';

  return (
    <div className="space-y-4">
    <Panel
      title="Seat map"
      description={description}
      actions={
        <div className="flex gap-1" role="tablist" aria-label="Seat map view">
          <Button role="tab" aria-selected={view === 'map'} variant={view === 'map' ? 'primary' : 'secondary'} onClick={() => setView('map')}>
            Map
          </Button>
          {canEditStructure ? (
            <Button
              disabled={!nextUndo || undo.isPending}
              onClick={() => undo.mutate()}
              title={nextUndo ? `Undo: ${nextUndo.summary}` : 'Nothing to undo'}
            >
              {nextUndo ? `Undo ${nextUndo.summary.toLowerCase()}` : 'Undo'}
            </Button>
          ) : null}
          <Button role="tab" aria-selected={view === 'list'} variant={view === 'list' ? 'primary' : 'secondary'} onClick={() => setView('list')}>
            List
          </Button>
        </div>
      }
    >
      <div className="px-5 py-4">
        {placing && !addingSeatTo ? (
          <p className="mb-2 rounded border border-accent/30 bg-accent-soft px-3 py-1.5 text-sm text-accent">Click on the map where the first seat (or the table center) should go.</p>
        ) : null}
        {addingSeatTo ? (
          <p className="mb-2 rounded border border-accent/30 bg-accent-soft px-3 py-1.5 text-sm text-accent">
            Click on the map where the new seat in {addingSeatTo.label} goes. Past either end it continues the numbering; in between, you will be asked its number.{' '}
            <button type="button" className="underline" onClick={() => setAddingSeatTo(null)}>
              Cancel
            </button>
          </p>
        ) : null}
        {linking ? (
          <p className="mb-2 rounded border border-accent/30 bg-accent-soft px-3 py-1.5 text-sm text-accent">
            Now click the wheelchair seat this companion belongs with.{' '}
            <button type="button" className="underline" onClick={() => setLinking(null)}>
              Cancel
            </button>
          </p>
        ) : null}

        {/* Outlines are drawings, so they are editable wherever seat
            attributes are — including on a published room (D-183). */}
        {canEditAttributes && view === 'map' ? (
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-2 text-ink-900">
              <span>Boundary of</span>
              <select
                value={boundary?.target.id ?? ''}
                onChange={(event) => {
                  const target = boundaryOptions.find((option) => option.id === event.target.value) ?? null;
                  setBoundary(target ? { target, tool: null, at: 0 } : null);
                }}
                className={INPUT}
              >
                <option value="">— choose —</option>
                {boundaryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
            {boundary ? (
              <>
                <span className="text-ink-400">{boundaryStatus(boundary.target)}</span>
                {!boundary.tool ? (
                  <>
                    {/* Which of the pool's shapes, when there is more than one. */}
                    {shapesFor(boundary.target).length > 1 ? (
                      <select
                        value={typeof boundary.at === 'number' ? boundary.at : 0}
                        onChange={(event) => setBoundary({ ...boundary, at: Number(event.target.value) })}
                        className={INPUT}
                        aria-label="Which shape"
                      >
                        {shapesFor(boundary.target).map((_, index) => (
                          <option key={index} value={index}>
                            Shape {index + 1}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <Button onClick={() => setBoundary({ ...boundary, tool: { mode: 'draw' }, at: shapesFor(boundary.target).length === 0 ? 'append' : (boundary.at ?? 0) })}>
                      {shapesFor(boundary.target).length === 0 ? 'Draw one' : 'Redraw this shape'}
                    </Button>
                    {/*
                      Several shapes, one pool (D-184). A lawn split by a
                      walkway is one capacity drawn twice: the same tickets, the
                      same price, sold from whichever the customer clicks.
                    */}
                    <Button onClick={() => setBoundary({ ...boundary, tool: { mode: 'draw' }, at: 'append' })}>Add another shape</Button>
                    {shapesFor(boundary.target).length > 0 ? (
                      <Button onClick={() => setBoundary({ ...boundary, tool: { mode: 'edit', points: shapesFor(boundary.target)[typeof boundary.at === 'number' ? boundary.at : 0] ?? [] } })}>
                        Edit corners
                      </Button>
                    ) : null}
                    {shapesFor(boundary.target).length > 1 ? (
                      <Button onClick={() => saveBoundary.mutate({ target: boundary.target, points: null, at: typeof boundary.at === 'number' ? boundary.at : 0 })} disabled={saveBoundary.isPending}>
                        Remove this shape
                      </Button>
                    ) : null}
                    {boundary.target.kind === 'section' && !derived(boundary.target) && shapesFor(boundary.target).length > 0 ? (
                      <Button onClick={() => saveBoundary.mutate({ target: boundary.target, points: null, at: 'all' })} disabled={saveBoundary.isPending}>
                        Follow the seats instead
                      </Button>
                    ) : null}
                  </>
                ) : boundary.tool.mode === 'edit' ? (
                  <>
                    <Button variant="primary" onClick={() => saveBoundary.mutate({ target: boundary.target, points: (boundary.tool as { points: Point[] }).points, at: boundary.at })} disabled={saveBoundary.isPending}>
                      {saveBoundary.isPending ? 'Saving…' : 'Save boundary'}
                    </Button>
                    <Button onClick={() => setBoundary({ ...boundary, tool: null })}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className="text-accent">Click corners on the map; close the shape to finish.</span>
                    <Button onClick={() => setBoundary({ ...boundary, tool: null })}>Cancel</Button>
                  </>
                )}
              </>
            ) : null}
          </div>
        ) : null}

        {view === 'map' ? (
          <SeatMapView
            layout={geometry}
            mode={canEditAttributes ? 'edit' : 'view'}
            backgroundUrl={layout.manifest.background_url}
            selectedSeatIds={selection.selected}
            onSeatClick={onSeatClick}
            onMarqueeSelect={canEditAttributes ? selection.marquee : undefined}
            onCanvasClick={canvasClickActive ? onCanvasClick : undefined}
            onSectionClick={
              canEditAttributes && !boundary?.tool
                ? (section) => {
                    // Clicking an outline opens it for editing straight away; a
                    // derived outline becomes drawn the moment it is saved.
                    const target: BoundaryTarget = { kind: 'section', id: section.id, name: `Section ${section.name}` };
                    const shapes = shapesOf(section.geometry_json);
                    setBoundary(shapes[0] ? { target, tool: { mode: 'edit', points: shapes[0] }, at: 0 } : { target, tool: null, at: 'append' });
                  }
                : undefined
            }
            onSeatsMoved={canEditAttributes ? (ids, dx, dy) => update.mutate({ ids, set: { dx, dy } }) : undefined}
            onRowMoved={
              canEditAttributes
                ? (rowId, dx, dy) => update.mutate({ ids: layout.seats.filter((seat) => seat.row_id === rowId).map((seat) => seat.id), set: { dx, dy } })
                : undefined
            }
            onRowExtend={canEditStructure ? (rowId, count, end) => extend.mutate({ rowId, count, end }) : undefined}
            onRowRename={canEditStructure ? (rowId) => askRenameRow(rowId) : undefined}
            selectedTableId={selectedTableId}
            onTableClick={canEditAttributes && !boundary?.tool ? (table) => { setSelectedTableId((current) => (current === table.id ? null : table.id)); selection.clear(); } : undefined}
            onTableMoved={canEditAttributes && !boundary?.tool ? (tableId, dx, dy) => { setSelectedTableId(tableId); moveTable.mutate({ tableId, set: { dx, dy } }); } : undefined}
            polygonTool={boundary?.tool ?? undefined}
            onPolygonChange={(points) => setBoundary((current) => (current ? { ...current, tool: { mode: 'edit', points } } : current))}
            onPolygonComplete={(points) => setBoundary((current) => (current ? { ...current, tool: { mode: 'edit', points } } : current))}
            className="overflow-hidden rounded border border-line"
          />
        ) : (
          <SeatList
            layout={geometry}
            selectedSeatIds={selection.selected}
            {...(canEditAttributes ? { onToggleSeat: selection.toggleSeat } : {})}
            className="max-h-[36rem] overflow-y-auto"
          />
        )}

        {editing ? (
          <div className="mt-3 rounded border border-line bg-page/60 p-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="text-sm font-semibold text-ink-900">
                {selectedSeats.length} seat{selectedSeats.length === 1 ? '' : 's'} selected
                {single ? (
                  <span className="ml-2 font-normal text-ink-600">
                    {sectionName(layout, single.section_id)} · {singleRow ? `row ${singleRow.label}` : singleTable ? `table ${singleTable.label}` : ''} · seat {single.seat_number}
                  </span>
                ) : null}
              </span>
              {/*
                Straightening a room one seat at a time is an hour a room. Only
                shown for a real selection, because aligning one seat to itself
                is not an operation.
              */}
              {canEditStructure && selectedSeats.length > 1 ? (
                <span className="flex flex-wrap items-center gap-1">
                  <Button disabled={arrange.isPending} onClick={() => arrange.mutate({ seat_ids: ids(), operation: 'ALIGN', align: 'TOP' })}>
                    Align top
                  </Button>
                  <Button disabled={arrange.isPending} onClick={() => arrange.mutate({ seat_ids: ids(), operation: 'ALIGN', align: 'LEFT' })}>
                    Align left
                  </Button>
                  <Button disabled={arrange.isPending || selectedSeats.length < 3} onClick={() => arrange.mutate({ seat_ids: ids(), operation: 'DISTRIBUTE', distribute: 'HORIZONTAL' })}>
                    Space across
                  </Button>
                  <Button disabled={arrange.isPending || selectedSeats.length < 3} onClick={() => arrange.mutate({ seat_ids: ids(), operation: 'DISTRIBUTE', distribute: 'VERTICAL' })}>
                    Space down
                  </Button>
                  <Button disabled={arrange.isPending} onClick={() => arrange.mutate({ seat_ids: ids(), operation: 'ROTATE', degrees: 90 })}>
                    Turn 90°
                  </Button>
                  <Button disabled={arrange.isPending} onClick={() => arrange.mutate({ seat_ids: ids(), operation: 'FLIP', flip: 'HORIZONTAL' })}>
                    Flip
                  </Button>
                  <Button disabled={renumber.isPending} onClick={askRenumber}>
                    Renumber…
                  </Button>
                </span>
              ) : null}
              {selectedSeats.length === 1 ? (
                <Button disabled={update.isPending} onClick={askRename}>
                  Rename seat…
                </Button>
              ) : null}
              {selectedRowId ? (
                <Button disabled={renameRow.isPending} onClick={() => askRenameRow(selectedRowId)}>
                  Rename row…
                </Button>
              ) : null}
              <Flag label="Wheelchair" flag="accessible" />
              <Flag label="Companion" flag="companion" />
              <Flag label="Restricted view" flag="restricted_view" />
              {/* A selling point rather than a warning (D-157): customers
                  filter for it, and it changes nothing about availability. */}
              <Flag label="Aisle seat" flag="aisle" />
              <Flag label="Obstructed view" flag="obstructed_view" />
              <Flag label="Production kill" flag="production_kill_capable" />
              {single?.companion === 1 ? (
                <Button onClick={() => setLinking(single.id)} disabled={link.isPending}>
                  {single.companion_for_seat_id ? 'Re-link to a wheelchair seat' : 'Link to a wheelchair seat'}
                </Button>
              ) : null}
              <Button onClick={selection.clear}>Clear selection</Button>
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="flex items-end gap-2 text-sm text-ink-900">
                <span className="pb-1.5">Note</span>
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Partial view behind the net" className={`w-72 ${INPUT}`} />
              </label>
              <Button onClick={() => updateSelection({ public_note: note.trim() || null })} disabled={update.isPending}>
                Set note
              </Button>
              <span className="mx-2 text-ink-400">|</span>
              <label className="flex items-end gap-2 text-sm text-ink-900">
                <span className="pb-1.5">Nudge by</span>
                <input value={step} onChange={(e) => setStep(e.target.value.replace(/\D/g, ''))} inputMode="numeric" className={`tabular w-14 ${INPUT}`} />
              </label>
              <div className="flex gap-1">
                <Button aria-label="Move left" onClick={() => updateSelection({ dx: -Number(step || 1) })} disabled={update.isPending}>
                  ←
                </Button>
                <Button aria-label="Move up" onClick={() => updateSelection({ dy: -Number(step || 1) })} disabled={update.isPending}>
                  ↑
                </Button>
                <Button aria-label="Move down" onClick={() => updateSelection({ dy: Number(step || 1) })} disabled={update.isPending}>
                  ↓
                </Button>
                <Button aria-label="Move right" onClick={() => updateSelection({ dx: Number(step || 1) })} disabled={update.isPending}>
                  →
                </Button>
              </div>
              <label className="flex items-end gap-2 text-sm text-ink-900">
                <span className="pb-1.5">Rotation °</span>
                <input value={rotation} onChange={(e) => setRotation(e.target.value.replace(/[^\d.-]/g, ''))} inputMode="numeric" className={`tabular w-16 ${INPUT}`} />
              </label>
              <Button onClick={() => updateSelection({ rotation: Number(rotation || 0) })} disabled={update.isPending}>
                Set rotation
              </Button>
              {canEditStructure ? (
                <>
                  <span className="mx-2 text-ink-400">|</span>
                  {single && (singleRow || singleTable) ? (
                    <Button
                      onClick={() =>
                        setAddingSeatTo(
                          singleRow ? { row_id: singleRow.id, label: `row ${singleRow.label}` } : { table_id: singleTable!.id, label: `table ${singleTable!.label}` },
                        )
                      }
                      disabled={addSeat.isPending}
                    >
                      Add a seat to {singleRow ? `row ${singleRow.label}` : `table ${singleTable!.label}`}
                    </Button>
                  ) : null}
                  <Button
                    variant="danger"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove ${selectedSeats.length} seat(s) from this draft?`)) remove.mutate();
                    }}
                  >
                    Remove seats
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
    {problems.length > 0 ? (
      <Panel title={`${problems.length} thing${problems.length === 1 ? '' : 's'} to look at`} description="The room checked against itself. Each one is something a customer would hit.">
        <ul className="space-y-2 px-5 py-4 text-sm">
          {problems.map((problem) => (
            <li key={problem.code + problem.message} className="flex items-start gap-2">
              <span className={`mt-0.5 rounded px-1.5 py-0.5 text-xs ${problem.severity === 'error' ? 'bg-bad-soft text-bad' : 'bg-warn-soft text-ink-700'}`}>{problem.severity}</span>
              <span className="text-ink-700">
                {problem.message}
                {problem.seat_ids.length > 0 ? (
                  <button type="button" className="ml-2 text-brand underline" onClick={() => selection.selectMany(problem.seat_ids)}>
                    Select {problem.seat_ids.length}
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    ) : null}
    {/* Below the map, because it is judged by looking at the map. */}
    {canEditStructure ? <CanvasPanel manifestId={manifestId} layout={geometry} onChanged={onChanged} onError={onError} onPreview={setCanvasPreview} /> : null}
    </div>
  );
}

function sectionName(layout: ManifestLayout, sectionId: string): string {
  return layout.sections.find((section) => section.id === sectionId)?.name ?? '';
}
